import { jwtDecode, JwtPayload } from 'jwt-decode'
import { DateTime } from 'luxon'

// Backoff schedule for re-attempting bootstrap-token acquisition: 15s, 30s, 60s, then 120s per
// attempt. Office SSO (Office.auth.getAccessToken) returns the same cached token until it is
// almost expired, so a stale return is expected near expiry — we must wait for Outlook's cache
// to roll over instead of retrying tightly, which triggers throttling error 13013 (see HAM logs
// 1000-windows-classic-login-failed).
export const BOOTSTRAP_RETRY_BASE_MS = 15_000
export const BOOTSTRAP_RETRY_MAX_MS = 120_000
// First attempt number whose delay equals the cap: 15s * 2^(4-1) = 120s
export const BOOTSTRAP_RETRY_CAP_ATTEMPT = 4

export const bootstrapRetryDelayMs = (attempt: number): number =>
    Math.min(BOOTSTRAP_RETRY_MAX_MS, BOOTSTRAP_RETRY_BASE_MS * 2 ** Math.max(0, attempt - 1))

// Office.js error 13013: "This API has been throttled to slow the call frequency."
export const OFFICE_THROTTLE_ERROR_CODE = 13013

export const isOfficeThrottleError = (error: unknown): boolean =>
    (error as { code?: unknown } | undefined)?.code === OFFICE_THROTTLE_ERROR_CODE

export type BootstrapTokenStatus = 'fresh' | 'stale' | 'invalid'

/**
 * Classify a bootstrap token: 'invalid' when it is empty, undecodable or carries no `exp`;
 * 'stale' when it expires within `freshnessBufferMs`; 'fresh' otherwise. Only a 'fresh' token
 * may be adopted and drive the refresh timer from its real expiry.
 */
export const classifyBootstrapToken = (token: string | undefined, freshnessBufferMs: number): BootstrapTokenStatus => {
    if (!token) {
        return 'invalid'
    }
    let decoded: JwtPayload | undefined
    try {
        decoded = jwtDecode<JwtPayload>(token)
    } catch (e) {
        console.error(e)
        return 'invalid'
    }
    if (!decoded?.exp) {
        return 'invalid'
    }
    const remainingMs = DateTime.fromSeconds(decoded.exp, { zone: 'UTC' }).toMillis() - DateTime.now().toUTC().toMillis()
    return remainingMs > freshnessBufferMs ? 'fresh' : 'stale'
}
